// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import { isEqual } from 'lodash';
import { AppComponentProps, SetupStep, SetupStepType, SetupViewProps } from '../../../typescriptHelpers';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { useForm } from 'antd/lib/form/Form';
import { AutomationTip, HelpTip, InfoTip, setupModuleStyle } from '../shared';
import {
    AppPlatform,
    AppStoreType,
    IntegrationCapability,
    IntegrationProviderAmazon,
    IntegrationProviderApple,
    IntegrationProviderGoogle,
    IntegrationProviderHuawei,
    IntegrationProviderSamsung,
    IntegrationType,
} from '../../../types';
import { tryUpdateIntegrationRequest, UpdateIntegrationRequest } from '../../../actions/integrations';
import {
    integrationUpdatableForIntegrationType,
    validateUpdateIntegrationValues,
} from '../../../helpers/integrationHelpers';
import { RootState } from '../../../reducers/reducers';
import {
    selectAppStoreId,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectOrgProviders,
    selectUserIsAdmin,
    selectWasAppCreatedAfterQualitativeOnboarding,
} from '../../../selectors/selectors';
import { iconComponentForPlatform } from '../../../helpers/platformComponentHelpers';
import {
    getError,
    getIntegrationTypeForAppStoreType,
    getIsAmazonAppStore,
    getIsAppleAppStore,
    getIsHuaweiAppGallery,
    getIsLoading,
    getIsSamsungGalaxyStore,
    getUpdated,
} from '../../../helpers';
import { displayAppStoreIdTypeForAppStoreType, displayStoreNameForAppStoreType } from '../../../helpers/displayHelpers';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { globalMessage } from '../../../utils/globalMessage';

const formField = (integrationType, integrationProvider) => {
    switch (integrationType) {
        case IntegrationType.Apple: {
            const provider = integrationProvider as IntegrationProviderApple;
            const availableProjects = provider?.data?.availableApps;
            return (
                <Form.Item
                    shouldUpdate
                    key={'bundleId'}
                    name={'bundleId'}
                    style={{ flex: 1, maxWidth: 360, margin: '15px 0px' }}
                    rules={[{ required: true, message: 'Please input a bundle ID.' }]}
                >
                    <Select disabled={!provider} placeholder="Bundle ID">
                        {availableProjects?.map((info) => {
                            return (
                                <Select.Option value={info.bundleId} key={info.bundleId}>
                                    {info.bundleId}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            );
        }
        case IntegrationType.Amazon: {
            const provider = integrationProvider as IntegrationProviderAmazon;
            return (
                <Form.Item
                    shouldUpdate
                    key={'appId'}
                    name={'appId'}
                    style={{ flex: 1, maxWidth: 360, margin: '15px 0px' }}
                    rules={[{ required: true, message: 'Please input an app identifier.' }]}
                >
                    <Input placeholder="App identifier" allowClear disabled={!provider} autoComplete="off" />
                </Form.Item>
            );
        }

        case IntegrationType.HuaweiAppGallery: {
            const provider = integrationProvider as IntegrationProviderHuawei;
            return (
                <Form.Item
                    shouldUpdate
                    key={'appId'}
                    name={'appId'}
                    style={{ flex: 1, maxWidth: 360, margin: '15px 0px' }}
                    rules={[{ required: true, message: 'Please input an app identifier.' }]}
                >
                    <Input placeholder="App ID" allowClear disabled={!provider} autoComplete="off" />
                </Form.Item>
            );
        }

        case IntegrationType.Samsung: {
            const provider = integrationProvider as IntegrationProviderSamsung;
            const availableProjects = provider?.data?.availableApps;
            return (
                <Form.Item
                    shouldUpdate
                    key={'contentId'}
                    name={'contentId'}
                    style={{ flex: 1, maxWidth: 360, margin: '15px 0px' }}
                    rules={[{ required: true, message: `Please input an app's content ID.` }]}
                >
                    <Select disabled={!provider} placeholder="Content ID">
                        {availableProjects?.map((info) => {
                            return (
                                <Select.Option value={info.contentId} key={info.contentId}>
                                    {info.name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            );
        }

        default: {
            const provider = integrationProvider as IntegrationProviderGoogle;
            return (
                <Form.Item
                    shouldUpdate
                    key={'packageName'}
                    name={'packageName'}
                    style={{ flex: 1, maxWidth: 360, margin: '15px 0px' }}
                    rules={[{ required: true, message: 'Please input a package name.' }]}
                >
                    <Input placeholder="Package name" allowClear disabled={!provider} autoComplete="off" />
                </Form.Item>
            );
        }
    }
};

const SetupViewComponent: React.FC<SetupViewProps> = ({ orgId, appId, onChange }) => {
    const dispatch = useDispatch();
    const [form] = useForm();

    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, orgId);
        const provider = integrationProviders.find((provider) => provider.id === appStoreIntegration?.providerId);

        return provider ?? null;
    });

    const integrationInstallationId = appStoreIntegration?.installationId;
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const integrationId = getIntegrationTypeForAppStoreType(appStoreType);
    const updatableType = integrationUpdatableForIntegrationType(integrationId);
    const idName = displayAppStoreIdTypeForAppStoreType(appStoreType);

    const updateIntegrationIsLoading = useSelector((state: RootState) =>
        getIsLoading(updatableType, state, integrationInstallationId),
    );
    const updateIntegrationSuccess = useSelector((state: RootState) =>
        getUpdated(updatableType, state, integrationInstallationId),
    );
    const updateIntegrationError = useSelector((state: RootState) =>
        getError(updatableType, state, integrationInstallationId),
    );

    React.useEffect(() => {
        if (updateIntegrationSuccess) {
            globalMessage.success(`${idName} updated!`);
            onChange(false);
        }
    }, [updateIntegrationSuccess]);

    React.useEffect(() => {
        if (updateIntegrationError) {
            globalMessage.error(updateIntegrationError);
        }
    }, [updateIntegrationError]);

    const updateIntegration = (values) => {
        const requestData = validateUpdateIntegrationValues(integrationId, integrationProvider, values);
        const updateRequest: UpdateIntegrationRequest = {
            orgId: orgId,
            appId: appId,
            integrationId: integrationInstallationId,
            data: requestData,
        };

        if (updatableType) {
            dispatch(tryUpdateIntegrationRequest(updatableType, updateRequest));
        } else {
            globalMessage.error('This integration does not support updating.');
        }
    };

    let formFieldKey: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            formFieldKey = 'bundleId';
            break;
        case AppStoreType.Amazon:
        case AppStoreType.Huawei:
            formFieldKey = 'appId';
            break;
        case AppStoreType.Samsung:
            formFieldKey = 'contentId';
            break;
        default:
            formFieldKey = 'packageName';
            break;
    }

    const defaultAppStoreIdValue = useSelector((state: RootState) => selectAppStoreId(state, appId, integrationId));

    const initialValues = {
        [formFieldKey]: defaultAppStoreIdValue,
    };

    const checkIfHasUnsavedChanges = () => {
        const formValues = form.getFieldsValue();
        return !isEqual(formValues, initialValues);
    };

    const field = formField(integrationId, integrationProvider);
    let label: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            label = 'Select app bundle ID';
            break;
        case AppStoreType.Amazon:
        case AppStoreType.Huawei:
            label = 'Confirm app identifier';
            break;
        case AppStoreType.Samsung:
            label = 'Select app content ID';
            break;
        default:
            label = 'Confirm app package name';
            break;
    }

    React.useEffect(() => {
        form.setFieldsValue({
            [formFieldKey]: defaultAppStoreIdValue,
        });
    }, [defaultAppStoreIdValue]);

    return (
        <Form
            form={form}
            initialValues={initialValues}
            onFinish={updateIntegration}
            onFieldsChange={() => onChange(checkIfHasUnsavedChanges())}
            style={{ ...setupModuleStyle }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Typography.Text style={{ color: antGrey.grey8, fontSize: 16, fontWeight: 400 }}>
                        {label}
                    </Typography.Text>
                    {!integrationProvider && (
                        <Tooltip title={`You must connect to the App Store before adding a ${idName}`}>
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    )}
                </div>
            </Space>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 14,
                }}
            >
                {field}
                <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                    {() => (
                        <Button
                            loading={updateIntegrationIsLoading}
                            disabled={
                                !form.isFieldsTouched(true) ||
                                !checkIfHasUnsavedChanges() ||
                                !integrationProvider ||
                                form.getFieldError([formFieldKey]).length > 0
                            }
                            type="primary"
                            htmlType="submit"
                            icon={<ArrowRightOutlined />}
                        >
                            Save
                        </Button>
                    )}
                </Form.Item>
            </div>
        </Form>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = ({ appId }) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    let appStoreUsageText: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            appStoreUsageText =
                'Runway relies on its integration with the App Store to track the completion of release cycles, automate build submissions, update phased release settings, and more.';
            break;
        case AppStoreType.Google:
            appStoreUsageText =
                'Runway relies on its integration with the Play Store to track the progress of release cycles, automate build submissions, manage staged rollout, and more.';
            break;
        case AppStoreType.Amazon:
            appStoreUsageText =
                'Runway relies on its integration with the Amazon Appstore to track the progress of release cycles, automate build submissions, and more.';
            break;
        case AppStoreType.Huawei:
            appStoreUsageText =
                'Runway relies on its integration with the Huawei AppGallery to track the progress of release cycles, update phased release settings, and more.';
            break;
        case AppStoreType.Samsung:
            appStoreUsageText =
                'Runway relies on its integration with the Samsung Galaxy Store to track the progress of release cycles, automate build submissions, and more.';
            break;
        default:
            appStoreUsageText =
                'Runway relies on its integration with the app store to track the progress of release cycles, automate build submissions, and more.';
            break;
    }

    let submitOrPauseText: string;
    switch (appStoreType) {
        case AppStoreType.Apple:
            submitOrPauseText =
                'With an App Store integration connected, Runway can automatically submit your app for review, pause a phased release when stability drops, and a ton more!';
            break;
        case AppStoreType.Google:
            submitOrPauseText =
                'With a Play Store integration connected, Runway can automatically submit your app for review, roll out a staged release over multiple days (or pause it if stability drops), and a ton more!';
            break;
        case AppStoreType.Amazon:
            submitOrPauseText =
                'With an Amazon Appstore integration connected, Runway can automatically submit your app for review, and more!';
            break;
        case AppStoreType.Huawei:
            submitOrPauseText =
                'With a Huawei AppGallery integration connected, Runway can automatically submit your app for review, pause a phased release when stability drops, and a ton more!';
            break;
        case AppStoreType.Samsung:
            submitOrPauseText =
                'With a Samsung Galaxy Store integration connected, Runway can automatically submit your app for review, and more!';
            break;
        default:
            submitOrPauseText =
                'With an app store integration connected, Runway can automatically submit your app for review, and more!';
            break;
    }

    return (
        <Space direction="vertical">
            <InfoTip>
                {
                    <span>
                        {appStoreUsageText}
                        <br />
                        <br />
                        tl;dr — Runway is way more useful with the {displayStoreNameForAppStoreType(appStoreType)}{' '}
                        connected!
                    </span>
                }
            </InfoTip>
            <InfoTip>
                {getIsAppleAppStore(appStoreType) ?
                    <span>
                        You can find your app’s bundle identifier under the “General” tab of your main app target in
                        your app’s Xcode project.
                    </span>
                : getIsAmazonAppStore(appStoreType) ?
                    <span>
                        You can find your app’s App ID by navigating to <b>Apps & Services {`>`} My Apps</b> in the
                        Amazon Developer Portal and selecting your app. Then under <b>App Information</b> scroll down to
                        the App ID field.
                    </span>
                : getIsHuaweiAppGallery(appStoreType) ?
                    <span>
                        You can find your app’s App ID by navigating to <b>My Apps {`>`} Android</b> in AppGallery
                        Connect and selecting your app. Then select <b>App information</b> in the sidebar to find the
                        App ID field.
                    </span>
                : getIsSamsungGalaxyStore(appStoreType) ?
                    <span>
                        You can find your app’s content ID by navigating to <b>Apps</b> in the Samsung Seller Portal.
                        You’ll find the content ID in the row for your app.
                    </span>
                :   <span>
                        You can find your app’s package name in the <Text code>build.gradle</Text> file for your app.
                        Most often it’s found under <Text code>defaultConfig</Text> {`->`}{' '}
                        <Text code>applicationId</Text>.
                    </span>
                }
            </InfoTip>
            <AutomationTip>{<span>{submitOrPauseText}</span>}</AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

const title = (_: AppPlatform, appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'Confirm your app’s bundle ID.';
        case AppStoreType.Huawei:
        case AppStoreType.Amazon:
            return `Confirm your app's app ID.`;
        case AppStoreType.Google:
            return "Confirm your app's package name.";
        case AppStoreType.Samsung:
            return 'Confirm your app’s content ID.';
        default:
            return '';
    }
};

const description = (appStoreType: AppStoreType) => {
    const storeName = displayStoreNameForAppStoreType(appStoreType);
    return `We’ll need an accurate app identifier to get going. Connect to the ${storeName} now and we’ll verify that it’s correct (and lots more).`;
};

const icon = (platform: AppPlatform) => {
    return iconComponentForPlatform(platform);
};

const bannerTitle = (appStoreType: AppStoreType) => {
    const storeName = displayStoreNameForAppStoreType(appStoreType);
    return `Recommended: Connect to the ${storeName}`;
};

export const AppStoreStep: SetupStep = {
    title,
    description,
    icon,
    SetupViewComponent,
    InfoViewComponent,
    integrationsRecommendationBannerData: {
        title: bannerTitle,
        description: 'Runway can streamline and automate several parts of the testing, submission and release process.',
        actionText: 'OK, let’s do it',
        type: SetupStepType.AppStore,
        integrationCapabilities: [IntegrationCapability.AppStore],
    },
    listTitle: 'Confirm your app identifier',
    listDescription: 'Runway will need an accurate app identifier.',
    isCompleteSelector: (root, { appId }) => {
        const appStoreType = selectAppStoreType(root, appId);
        const isUserAdmin = selectUserIsAdmin(root);
        const integrationId = getIntegrationTypeForAppStoreType(appStoreType);
        const defaultAppStoreIdValue = selectAppStoreId(root, appId, integrationId);
        const wasVisited = getSetupStepLocalData(appId, SetupStepType.AppStore)?.hasBeenVisited;

        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);

        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ?
                !!defaultAppStoreIdValue && wasVisited
            :   !!defaultAppStoreIdValue;
    },
};
